import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DistrictSchemas, type PopulatedDistrict } from "@workspace/contracts/districts";
import { Button } from "@workspace/ui/components/button";
import { useState } from "react";
import { orpc } from "@/api/orpc";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableActionBar } from "@/components/data-table/data-table-action-bar";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { DeleteDistrict } from "@/features/district/delete";
import { useDistrictsColumns } from "@/features/district/use-district-columns";
import { useDataTable } from "@/hooks/use-data-table";
import { Main } from "@/layout/main";
import { withDashboardPaginationDefaults } from "@/lib/pagination";
import type { DataTableRowAction } from "@/types/data-table";

export const Route = createFileRoute("/_authenticated/districts/")({
  validateSearch: (search) => DistrictSchemas.list.parse(withDashboardPaginationDefaults(search)),
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { orpc, queryClient } }) => {
    await queryClient.ensureQueryData(orpc.districts.list.queryOptions({ input: { ...deps, all: true } }));
    return;
  },
});

function RouteComponent() {
  const search = Route.useSearch();

  const [rowAction, setRowAction] = useState<DataTableRowAction<PopulatedDistrict> | null>(null);

  const columns = useDistrictsColumns({ setRowAction });

  const query = useSuspenseQuery(orpc.districts.list.queryOptions({ input: { ...search, all: true } }));

  const { table } = useDataTable({
    columns: columns,
    data: query.data.items,
    pageCount: query.data.totalPageNumbers,
    debounceMs: 500,
    getRowId: (originalRow) => originalRow.id,
    enableSorting: true,
    initialState: { columnVisibility: { slug: false } },
  });

  return (
    <Main>
      <DataTable table={table} actionBar={<DataTableActionBar table={table} />}>
        <DataTableToolbar table={table}>
          <Button variant="outline" size="sm" asChild>
            <Link to="/districts/new">Create District</Link>
          </Button>
        </DataTableToolbar>
      </DataTable>

      <DeleteDistrict
        open={rowAction?.variant === "delete"}
        district={rowAction?.row.original}
        onClose={() => setRowAction(null)}
      />
    </Main>
  );
}
